import { createInPlaceSaveApi } from "#/admin/page_builder/api/in-place-save";

// Dispatched on the form when a submit becomes an in-place save. `detail.saving` is the save's promise.
export const IN_PLACE_SAVE_EVENT = "sparkle:in-place-save";

// Fields a publishing control adds to ask for more than a save: publishing, a preview or a QA request.
const ACTION_FIELD = /^(sync_action|dry_run)$|\[qa_requested\]$/;

// Fields the page writes itself: the URLs follow the tab and the preview, the lock version follows each save.
const IGNORED_FIELD = /^(return_to|preview_url)$|\[lock_version\]$/;

const FIRST_INTERACTION_EVENTS = ["pointerdown", "keydown"];

const TOGGLE_SELECTOR = 'input[type="checkbox"], input[type="radio"], sp-checkbox, sp-radio, sp-switch';

const PUBLISHING_CONTROLS_SELECTOR = ".seg-btn.publishing-controls";

// Returns whether a `return_to` value leads off the edit page: `/admin/pages` does, `/admin/pages/4297/edit/?tab=x`
// does not.
function leavesEditPage(returnTo) {
  if (returnTo === "") {
    return false;
  }

  const target = new URL(returnTo, location.href);

  return (
    target.origin !== location.origin ||
    withoutTrailingSlash(target.pathname) !== withoutTrailingSlash(location.pathname)
  );
}

function withoutTrailingSlash(path) {
  return path.replace(/\/+$/, "");
}

/**
 * Used to save the Page Builder edit form without reloading the edit page. A submit that becomes an in-place save
 * dispatches `IN_PLACE_SAVE_EVENT` on the form.
 *
 * A submit is saved in place only when it asks for nothing more than a save and no field of the form besides the Page
 * Builder output differs from the value it had at the user's first interaction with the page. A missing field counts
 * as empty. Every other submit, and every in-place save the server does not accept, goes through the form's regular
 * submit, whose reload renders errors, conflicts and the changes of the other fields.
 *
 * The first interaction is the baseline because custom elements register their values during startup, and nothing
 * before it is an edit. A submit with no earlier interaction counts as the first one. The comparison leaves out the
 * fields that change without an edit: `IGNORED_FIELD` and the tab selectors.
 *
 * Assistive technology can toggle a control with only a `click`, which the control handles before any listener runs,
 * so no baseline can hold its earlier value. A click on a toggle before the first interaction sends every later submit
 * through the regular submit.
 *
 * @param {Object} options
 * @param {HTMLFormElement} options.form - The primary form of the edit page.
 * @param {HTMLInputElement} options.output - The field the Page Builder serializes its components into.
 * @returns {{
 *   handleSubmit: (event: SubmitEvent) => Promise<boolean>|null,
 *   saveOnSubmit: (prepareSubmit: () => void) => void,
 * }}
 */
export function createInPlaceSave({ form, output }) {
  const api = createInPlaceSaveApi();
  let pending = null;
  let initialValues = null;
  let toggledBeforeBaseline = false;

  function captureInitialValues() {
    initialValues ??= fieldValues();
  }

  function recordToggleBeforeBaseline({ target }) {
    const toggle = target.closest?.(TOGGLE_SELECTOR);
    const inForm = toggle?.closest("form") === form;

    if (
      initialValues === null &&
      inForm &&
      toggle.getAttribute("name") &&
      !toggle.closest('[class~="tab-selectors"]')
    ) {
      toggledBeforeBaseline = true;
    }
  }

  // Returns the compared field values by name: `{ "page[title]": ["…"], "location_assignment[8]": ["2"] }`.
  function fieldValues() {
    const tabSelectors = form.querySelectorAll('[class~="tab-selectors"] [name]');
    const tabSelectorNames = new Set(Array.from(tabSelectors, (field) => field.getAttribute("name")));
    const values = {};

    for (const [name, value] of new FormData(form)) {
      if (name !== output.name && !IGNORED_FIELD.test(name) && !tabSelectorNames.has(name)) {
        values[name] = [...(values[name] ?? []), value instanceof File ? value.name : value];
      }
    }

    return values;
  }

  function fieldChanged() {
    captureInitialValues();

    const current = fieldValues();
    const names = new Set([...Object.keys(initialValues), ...Object.keys(current)]);

    return Array.from(names).some((name) => comparable(initialValues[name]) !== comparable(current[name]));
  }

  function comparable(values = []) {
    return JSON.stringify(values.filter((value) => value !== ""));
  }

  function requestsMoreThanASave() {
    const targetsAnotherWindow = form.target !== "" && form.target !== "_self";
    const data = new FormData(form);
    const returnsElsewhere = data.getAll("return_to").some(leavesEditPage);
    const names = Array.from(data.keys());

    return targetsAnotherWindow || returnsElsewhere || names.some((name) => ACTION_FIELD.test(name));
  }

  async function save() {
    try {
      const saved = await api.save(form.action, new FormData(form));

      // An expired session redirects to the login page, which answers HTML.
      if (typeof saved?.publishing_controls !== "string") {
        return false;
      }

      applySavedState(saved);

      return true;
    } catch {
      return false;
    }
  }

  function applySavedState({ lock_version: lockVersion, flash, publishing_controls: publishingControls }) {
    const lockVersionField = form.querySelector('[name$="[lock_version]"]');

    if (lockVersionField) {
      lockVersionField.value = lockVersion;
    }

    document.getElementById("flash-messages")?.insertAdjacentHTML("afterbegin", flash);

    const replacement = new DOMParser()
      .parseFromString(publishingControls, "text/html")
      .querySelector(PUBLISHING_CONTROLS_SELECTOR);
    const current = document.querySelector(PUBLISHING_CONTROLS_SELECTOR);

    if (replacement && current) {
      current.replaceWith(replacement);
    }
  }

  function submitRegularly() {
    HTMLFormElement.prototype.submit.call(form);
  }

  /**
   * Saves the form in place when the submit qualifies, and cancels the submit's navigation.
   *
   * @param {SubmitEvent} event
   * @returns {Promise<boolean>|null} Resolves with whether the form saved in place; on `false` the form has
   *   been submitted regularly. A submit while a save is pending is cancelled and returns the pending save.
   *   `null` when the submit does not qualify and proceeds untouched.
   */
  function handleSubmit(event) {
    if (pending) {
      event.preventDefault();

      return pending;
    }

    if (toggledBeforeBaseline || fieldChanged() || requestsMoreThanASave()) {
      return null;
    }

    event.preventDefault();
    pending = save().then(function submitUnlessSaved(saved) {
      if (!saved) {
        submitRegularly();
      }

      return saved;
    });
    pending.finally(function clearPending() {
      pending = null;
    });
    form.dispatchEvent(new CustomEvent(IN_PLACE_SAVE_EVENT, { detail: { saving: pending } }));

    return pending;
  }

  /**
   * Handles every later submit of the form with `handleSubmit`.
   *
   * @param {() => void} prepareSubmit - Writes the fields the submit sends, such as the Page Builder output.
   */
  function saveOnSubmit(prepareSubmit) {
    function prepareAndHandleSubmit(event) {
      prepareSubmit();
      handleSubmit(event);
    }

    form.addEventListener("submit", prepareAndHandleSubmit);
  }

  FIRST_INTERACTION_EVENTS.forEach((type) => {
    document.addEventListener(type, captureInitialValues, { capture: true, once: true });
  });
  document.addEventListener("click", recordToggleBeforeBaseline, { capture: true });

  return { handleSubmit, saveOnSubmit };
}
